import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Library, Plus, BookOpen } from "lucide-react";
import { toast } from "sonner";

export default function CourseLibrary() {
  const [courses, setCourses] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", institution: "" });

  const load = () => api.get("/courses").then(({ data }) => setCourses(data)).catch(() => setCourses([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Give the course a name.");
    try {
      await api.post("/courses", form);
      toast.success("Course added.");
      setOpen(false); setForm({ name: "", institution: "" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-8" data-testid="course-library">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Course library</h1>
          <p className="mt-1 text-muted-foreground">Organise your assignments by course.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-course-btn" className="bg-[#0B5D3B] text-white hover:bg-[#08472D]"><Plus className="mr-2 h-4 w-4" />New course</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add a course</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label htmlFor="cn">Course name</Label><Input id="cn" data-testid="course-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. LAW 402: Equity & Trusts" className="mt-1.5" /></div>
              <div><Label htmlFor="ci">Institution</Label><Input id="ci" data-testid="course-institution-input" value={form.institution} onChange={(e) => setForm({ ...form, institution: e.target.value })} placeholder="e.g. University of Toronto" className="mt-1.5" /></div>
            </div>
            <DialogFooter><Button onClick={create} data-testid="create-course-submit" className="bg-[#0B5D3B] text-white hover:bg-[#08472D]">Add course</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {courses === null ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}</div>
      ) : courses.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card py-20 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#E8F5EE] text-[#0B5D3B] dark:bg-emerald-950 dark:text-[#25D366]"><Library className="h-7 w-7" /></div>
          <h3 className="mt-4 font-display text-xl font-semibold">No courses yet</h3>
          <p className="mt-1 max-w-sm text-muted-foreground">Courses are created automatically when you start an assignment, or add one here.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {courses.map((c) => (
            <div key={c.id} data-testid={`course-card-${c.id}`} className="rounded-2xl border border-border bg-card p-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E8F5EE] text-[#0B5D3B] dark:bg-emerald-950 dark:text-[#25D366]"><BookOpen className="h-5 w-5" /></div>
              <h3 className="mt-4 font-display text-lg font-semibold leading-tight">{c.name}</h3>
              {c.institution && <p className="mt-1 text-sm text-muted-foreground">{c.institution}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
